'use client';
import { Form } from '@form/Form';
import FormSubmit from '@form/FormSubmit';
import { useApi } from '@hooks/useApi';
import { ComponentProps, ReactNode, useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';

interface Props<T = unknown> extends Partial<ComponentProps<typeof Form>> {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    url: string;
    appendedData?: Record<string, any>;
    submitTitle?: string;
    onSuccess?: (newModel: T) => any;
    onFailure?: () => any;
    submitClassName?: string;
    // replaces the default submit button, e.g. when its state depends on form values
    renderSubmit?: (isLoading: boolean) => ReactNode;
}

const ActionForm = <T = unknown,>({
    url,
    children,
    appendedData,
    onSuccess,
    submitTitle = 'Speichern',
    onFailure,
    method = 'POST',
    submitClassName,
    renderSubmit,
    ...props
}: Props<T>) => {
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = useCallback(
        (data: FieldValues) => {
            setIsLoading(true);
            return api({
                url,
                method,
                data: {
                    ...data,
                    ...appendedData
                },
                onBefore: () => setIsLoading(false),
                onSuccess,
                onFailure
            });
        },
        [appendedData, onSuccess, onFailure, method, url]
    );

    return (
        <Form onSubmit={handleSubmit} {...props}>
            {children}
            {renderSubmit ? (
                renderSubmit(isLoading)
            ) : (
                <FormSubmit className={submitClassName} isLoading={isLoading}>
                    {submitTitle}
                </FormSubmit>
            )}
        </Form>
    );
};

export default ActionForm;
